import { Button, Card, Flex, Heading, Text } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
// eslint-disable-next-line import/named
import { createRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { DemoBrandConfig } from "./components/DemoBrandConfig";
import { DemoImpersonationCard } from "./components/DemoImpersonationCard";
import { DemoKillerFlowCard } from "./components/DemoKillerFlowCard";
import { integrationSettingsRoute } from "./routes/integrationSettingsRoute";
import { knowledgeSettingRoute } from "./routes/knowledgeSettingRoute";

import {
  bootstrapDemoEnvironment,
  resetDemoEnvironment,
} from "../../services/demo";
import {
  isDemoEnterprise,
  isMeaningfulGigsOrDemo,
  isMeaningfulGigsRole,
} from "../../utils/auth";
import { useAppDispatch } from "../../utils/hooks";
import { errorMessage } from "../../utils/http";
import { setDemoPersona } from "../../utils/slices/auth";
import { authLayoutRoute } from "../auth-layout/route";

const { MODE } = import.meta.env;

export const settingsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "settings",

  component: Settings,
});

function Settings() {
  const navigate = useNavigate({ from: settingsRoute.to });
  const showDemoCard = isMeaningfulGigsOrDemo();
  // Bootstrap card: MG staff sitting on a real tenant. They need this
  // exactly when the demo enterprise doesn't exist yet on the stage —
  // i.e. the migrator hasn't been invoked. We surface it to any MG user
  // whose active enterprise isn't itself the demo (the demo Reset card
  // covers that case).
  const showBootstrapCard =
    isMeaningfulGigsRole() && !isDemoEnterprise() && MODE !== "production";

  const goToKnowledge = () => navigate({ to: knowledgeSettingRoute.to });
  const goToIntegrations = () => navigate({ to: integrationSettingsRoute.to });

  // Outer wrapper bounds the article to its grid cell
  // (`flex h-full flex-col overflow-hidden`). The article itself uses
  // PLAIN BLOCK FLOW (no `display: grid`, no `display: flex`) and just
  // scrolls vertically when content overflows. Cards stack as block
  // children at their natural heights via `space-y-6`. Anything
  // fancier (grid auto-rows, flex column with shrink) is what causes
  // the recurring "all cards equal-height + clipped" regression on
  // shorter viewports.
  return (
    <div
      data-testid="settings-page"
      className="flex h-full flex-col overflow-hidden"
    >
      <article className="flex-1 space-y-6 overflow-y-auto px-10 py-6">
        <header className="flex items-center justify-between">
          <Heading as="h1" size="7" weight="bold">
            Settings
          </Heading>
        </header>

        <Card
          data-testid="settings-card-knowledge"
          role="button"
          tabIndex={0}
          className="cursor-pointer"
          onClick={goToKnowledge}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              goToKnowledge();
            }
          }}
        >
          <Flex direction="column" gap="1">
            <Heading size="3" weight="bold">
              Knowledge Settings
            </Heading>
            <Text size="2" color="gray">
              Manage brand guidelines, reference files, and templates for AI
              reviews.
            </Text>
          </Flex>
        </Card>

        <Card
          data-testid="settings-card-integration"
          role="button"
          tabIndex={0}
          className="cursor-pointer"
          onClick={goToIntegrations}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              goToIntegrations();
            }
          }}
        >
          <Flex direction="column" gap="1">
            <Heading size="3" weight="bold">
              Integration Settings
            </Heading>
            <Text size="2" color="gray">
              Configure trigger events, comment behavior, and design review
              rules for Wrike and Asana integrations.
            </Text>
          </Flex>
        </Card>

        {showDemoCard ? <DemoResetCard /> : null}
        {showBootstrapCard ? <DemoBootstrapCard /> : null}
        {isDemoEnterprise() ? <DemoBrandConfig /> : null}
        {isDemoEnterprise() ? <DemoImpersonationCard /> : null}
        {isDemoEnterprise() ? <DemoKillerFlowCard /> : null}
      </article>
    </div>
  );
}

/**
 * One-click "Reset Demo Environment" card. MG-only — the backend handler
 * also refuses unless the active enterprise has Enterprise.isDemo=true,
 * so a misconfigured stage cannot wipe real customer data.
 */
function DemoResetCard() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "running" }
    | { kind: "done"; resetAt: string }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function onReset() {
    if (
      !window.confirm(
        "This will wipe and reseed the Vitalora demo data — tickets, comments, " +
          "agent activity, ROI dashboard, Slack messages. Real customer data is " +
          "untouched. Proceed?",
      )
    ) {
      return;
    }
    setState({ kind: "running" });
    try {
      const result = await resetDemoEnvironment();
      // Demo personas are display-only; clearing them on reset keeps the
      // post-reset session honest about who the demoer actually is.
      dispatch(setDemoPersona(null));
      // Wipe the React Query cache *before* showing the success flash.
      // Otherwise the cached Ask Puntt chats (and ticket list, ROI
      // dashboard, knowledge hub, etc.) stay visible in the sidebar
      // during the 400ms confirmation window until the reload fires,
      // which makes the reset feel half-applied. Clear + remove drops
      // all keys synchronously so subscribed components re-render to
      // their empty state immediately.
      queryClient.cancelQueries();
      queryClient.removeQueries();
      setState({ kind: "done", resetAt: result.resetAt });
      // Hard refresh so every cached query (Ask Puntt sidebar chats,
      // /tickets list, ROI dashboard, Knowledge Hub, etc.) re-fetches
      // against the freshly seeded state. Without this the in-memory
      // React Query cache stays warm and the sidebar still lists the
      // pre-reset chats until the user manually navigates. Short
      // setTimeout lets the green confirmation flash before reload.
      setTimeout(() => {
        window.location.reload();
      }, 400);
    } catch (e) {
      setState({
        kind: "error",
        message: await errorMessage(e),
      });
    }
  }

  return (
    <Card data-testid="settings-card-demo">
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="1">
          <Heading size="3" weight="bold">
            Demo Environment
          </Heading>
          <Text size="2" color="gray">
            Reset the Vitalora demo tenant — restore artwork, comments, briefs,
            claims, agent activity, and Slack content to their seeded state.
            Wipes data scoped to the demo enterprise only; real customer data is
            never touched. MG superusers only.
          </Text>
        </Flex>
        <Flex align="center" gap="3">
          <Button
            data-testid="settings-demo-reset-button"
            onClick={onReset}
            disabled={state.kind === "running"}
          >
            {state.kind === "running" ? "Resetting…" : "Reset Demo"}
          </Button>
          {state.kind === "done" ? (
            <Text size="2" color="green">
              Demo reset at {new Date(state.resetAt).toLocaleTimeString()}.
            </Text>
          ) : null}
          {state.kind === "error" ? (
            <Text size="2" color="red">
              {state.message}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Card>
  );
}

/**
 * "Bootstrap Vitalora Demo" card — visible to MG superusers on any
 * non-demo enterprise. Solves the chicken-and-egg where the migrator
 * Lambda hasn't been invoked on a stage and Sarah / Priya can't log in
 * because their docs don't exist yet. Idempotent on the backend, so
 * re-clicking on a stage that's already seeded just upserts.
 */
function DemoBootstrapCard() {
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "running" }
    | {
        kind: "done";
        completedAt: string;
        alreadyExisted: boolean;
        enterpriseId: string;
      }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function onBootstrap() {
    if (
      !window.confirm(
        "This seeds the Vitalora demo tenant on this stage — creates the " +
          "demo enterprise, 6 users (Sarah / Priya / James / Tom / Mia / Alex), " +
          "tickets, knowledge, integrations, and ROI data. Safe to run repeatedly. " +
          "Proceed?",
      )
    ) {
      return;
    }
    setState({ kind: "running" });
    try {
      const result = await bootstrapDemoEnvironment();
      setState({
        kind: "done",
        completedAt: result.completedAt,
        alreadyExisted: result.alreadyExisted,
        enterpriseId: result.enterpriseId,
      });
    } catch (e) {
      setState({
        kind: "error",
        message: await errorMessage(e),
      });
    }
  }

  return (
    <Card data-testid="settings-card-demo-bootstrap">
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="1">
          <Heading size="3" weight="bold">
            Bootstrap Vitalora Demo
          </Heading>
          <Text size="2" color="gray">
            Seeds the Vitalora demo tenant on this stage. Use this when{" "}
            <code>sarah.chen@vitalora.demo.puntt.ai</code> can&apos;t log in
            because the migrator hasn&apos;t been invoked yet. Idempotent — safe
            to re-run. MG superusers only.
          </Text>
        </Flex>
        <Flex align="center" gap="3">
          <Button
            data-testid="settings-demo-bootstrap-button"
            onClick={onBootstrap}
            disabled={state.kind === "running"}
          >
            {state.kind === "running" ? "Seeding…" : "Bootstrap Vitalora"}
          </Button>
          {state.kind === "done" ? (
            <Text size="2" color="green">
              {state.alreadyExisted
                ? `Re-seeded at ${new Date(state.completedAt).toLocaleTimeString()}.`
                : `Vitalora seeded at ${new Date(state.completedAt).toLocaleTimeString()} (enterprise ${state.enterpriseId.slice(-6)}). You can now log in as sarah.chen@vitalora.demo.puntt.ai.`}
            </Text>
          ) : null}
          {state.kind === "error" ? (
            <Text size="2" color="red">
              {state.message}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Card>
  );
}
